/**
 * Forecast Hooks
 *
 * Domain-specific hooks for forecast-related API calls.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { forecastsApi } from "@/api";
import type {
  ForecastDataParams,
  ForecastDataItem,
  ForecastDataByNodeId,
  ForecastAggregationDTO,
} from "@/api";

export interface UseForecastDataReturn {
  data: ForecastDataItem[];
  aggregation: ForecastAggregationDTO | null;
  loading: boolean;
  error: Error | null;
  isPlaceholderData: boolean;
}

/** Params for useForecastData - accepts forecast_date for single-date (maps to min_date/max_date) */
export type UseForecastDataParams = Partial<
  Omit<ForecastDataParams, "min_date" | "max_date"> & { forecast_date?: string }
>;

/**
 * Fetch forecast data for given parameters
 *
 * This hook fetches forecast data when all required parameters are provided.
 * It automatically refetches when any of the dependencies change.
 *
 * @param params - Partial forecast params (will skip if any required param is missing)
 * @returns { data, aggregation, loading, error } - Forecast items and optional aggregation (total row)
 */
export function useForecastData(
  params: UseForecastDataParams,
): UseForecastDataReturn {
  const {
    modelId,
    weatherModelId,
    geographicNodeIds,
    forecast_date,
    run,
    aggregationNodeId,
  } = params;

  // Skip if any required param is missing
  const isEnabled =
    !!modelId &&
    !!weatherModelId &&
    !!geographicNodeIds &&
    !!forecast_date &&
    !!run;

  const { data, isLoading, isFetching, error, isPlaceholderData } = useQuery({
    queryKey: [
      "forecasts",
      "grid",
      {
        modelId,
        weatherModelId,
        geographicNodeIds,
        forecast_date,
        run,
        aggregationNodeId,
      },
    ],
    queryFn: async () => {
      const dateStr = forecast_date!;
      const response = await forecastsApi.forecasts.list({
        modelId: modelId!,
        weatherModelId: weatherModelId!,
        geographicNodeIds: geographicNodeIds!,
        min_date: dateStr,
        max_date: dateStr,
        run: run!,
        limit: 200,
        aggregationNodeId,
      });
      return response;
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes (grid data updates with runs)
    placeholderData: keepPreviousData, // Keep showing old data while new query loads
  });

  return {
    data: data?.items || [],
    aggregation: data?.aggregation || null,
    loading: isLoading || isFetching,
    error: error as Error | null,
    isPlaceholderData,
  };
}

/**
 * Utility function to convert forecast data array to a map by geographic_node_id
 * for quick lookups when rendering map features
 */
export function createForecastDataMap(
  items: ForecastDataItem[] | null,
): ForecastDataByNodeId {
  if (!items || items.length === 0) return {};

  return items.reduce((map, item) => {
    map[item.geographic_node_id] = item;
    return map;
  }, {} as ForecastDataByNodeId);
}
